import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import { Organization, User } from '../models';
import { Operator, Query } from '../models/query';
import { OrganizationService } from '../services';

export const ORGANIZATION_FEATURE_KEY = 'organizations';

interface OrganizationEntity extends Organization {
  orgUsers: User['id'][];
}

const toEntity = (org: Organization, orgUsers: User['id'][] = []): OrganizationEntity => ({
  ...org,
  orgUsers,
});

interface OrganizationsState extends EntityState<OrganizationEntity> {
  loading: { [key: string]: boolean };
  error?: string;
  selectedId: Organization['id'] | null;
  count: number;
}

const organizationsAdapter = createEntityAdapter<OrganizationEntity>();

export const createInitialState = (partialState: Partial<OrganizationsState> = {}) => (
  organizationsAdapter.getInitialState({
    loading: {},
    selectedId: null,
    count: 0,
    projectCountByOrgId: {},
    projectsByOrgId: {},
    migrationStatus: {},
    ...partialState, // overwrite if necessary
  })
);

export const doFetchOrganizationByAccountId = createAsyncThunk(
  'organizations/fetchOrganizationsByAccountId',
  async (input: Required<Pick<Organization, 'accountId'>>) => {
    const orgCriteria: Array<Query<Organization>> = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: input.accountId,
      },
      {
        field: 'archived',
        operator: Operator.EQ,
        value: false,
      },
    ];

    return fetchData(orgCriteria);
  },
);

export const doFetchOrganizationById = createAsyncThunk(
  'organizations/fetchOrganizationsById',
  async (input: Required<Pick<Organization, 'id'>>) => {
    const orgCriteria: Array<Query<Organization>> = [
      {
        field: 'id',
        operator: Operator.EQ,
        value: input.id,
      },
      {
        field: 'archived',
        operator: Operator.EQ,
        value: false,
      },
    ];

    return fetchData(orgCriteria);
  },
);

const fetchData = async (orgCriteria: Array<Query<Organization>>) => {
  const orgResponse = await OrganizationService.getOrganizations(...orgCriteria);
  return { organizations: orgResponse.data.map(org => toEntity(org)) };
};

const organizationsSlice = createSlice({
  name: ORGANIZATION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doFetchOrganizationByAccountId.pending, state => {
      state.loading[doFetchOrganizationByAccountId.typePrefix] = true;
    });
    builder.addCase(doFetchOrganizationByAccountId.fulfilled, (state, action) => {
      organizationsAdapter.upsertMany(state, action.payload.organizations);
      state.loading[doFetchOrganizationByAccountId.typePrefix] = false;
    });
    builder.addCase(doFetchOrganizationByAccountId.rejected, (state, action) => {
      state.loading[doFetchOrganizationByAccountId.typePrefix] = false;
      state.error = action.error.message;
    });
    builder.addCase(doFetchOrganizationById.pending, state => {
      state.loading[doFetchOrganizationById.typePrefix] = true;
    });
    builder.addCase(doFetchOrganizationById.fulfilled, (state, action) => {
      organizationsAdapter.upsertMany(state, action.payload.organizations);
      state.loading[doFetchOrganizationById.typePrefix] = false;
    });
    builder.addCase(doFetchOrganizationById.rejected, (state, action) => {
      state.loading[doFetchOrganizationById.typePrefix] = false;
      state.error = action.error.message;
    });
  },
});

const selectOrganizationsFeature = (state: AppState) => state[ORGANIZATION_FEATURE_KEY];
const { selectAll, selectEntities } = organizationsAdapter.getSelectors(selectOrganizationsFeature);

export const selectSelectedId = createSelector(
  selectOrganizationsFeature,
  organizationsState => organizationsState.selectedId,
);

export const selectAllByAccountId = (accountId: number) => createSelector(
  selectAll,
  all => all
    .filter(organization => organization.accountId === accountId)
    .sort((x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime()),
);

export const selectSelectedOrganization = createSelector(
  selectEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : null),
);

export const selectFirstOrganization = (accountId: number) => createSelector(
  selectAll,
  all => all
    .filter(organization => organization.accountId === accountId)[0]
    || null,
);

export const selectOrganizationById = (id: Organization['id']) => createSelector(
  selectEntities,
  entities => entities[id],
);

export default organizationsSlice.reducer;
